/**
 * The live room's rules that need no DOM: the recording notice by age, how
 * a feed sits in its pane, the self view's size, whether a shared track is
 * showing frames, which calls someone is in, and the room chat's bounds.
 * Pure, so they are unit tested.
 */

/** The recording notice, keyed to the player's age on the profile. */
export function recordingNotice(age: number | null | undefined): { line: string; invite: string; minor: boolean } {
  const minor = typeof age === 'number' && age < 18;
  return minor
    ? {
        minor,
        line: 'Every lesson is recorded so you can rewatch it. A parent can watch any recording in the account.',
        invite: 'Invite a Parent',
      }
    : { minor, line: 'Every lesson is recorded so you can rewatch it.', invite: 'Invite Someone' };
}

export interface Box { w: number; h: number }

/**
 * A feed's box inside its pane: the feed's own aspect, as large as fits,
 * never cropped (Meet's one tile). A portrait phone feed on a laptop is a
 * tall tile; a laptop's feed on a phone is a wide one.
 */
export function fitBox(feed: Box | null, pane: Box): Box {
  if (!feed || !feed.w || !feed.h || !pane.w || !pane.h) return { w: pane.w, h: pane.h };
  const scale = Math.min(pane.w / feed.w, pane.h / feed.h);
  return { w: Math.round(feed.w * scale), h: Math.round(feed.h * scale) };
}

/**
 * The self tile, small in a corner as Meet's floating self-view (141, 147):
 * on a phone a quarter of the stage's width; on a larger screen a sixth of
 * it, 160 to 320 wide. At its own feed's aspect (tall for an upright phone,
 * wide for a laptop), 16:9 until the feed is known. It never takes more than
 * its share of the height it sits in, or the space above the phone's chat
 * sheet less a margin, narrowing to keep its aspect.
 */
export function selfTileSize(feed: Box | null, stage: Box, maxH: number, large = false): Box {
  const aspect = feed && feed.w && feed.h ? feed.h / feed.w : 9 / 16;
  let w = large ? Math.min(320, Math.max(160, Math.round(stage.w / 6))) : Math.round(stage.w / 4);
  let h = Math.round(w * aspect);
  if (maxH > 0 && h > maxH) { h = Math.round(maxH); w = Math.round(h / aspect); }
  return { w, h };
}

export type Corner = 'tl' | 'tr' | 'bl' | 'br';
/** The self tile's corners stand this far in from the edges of the space it sits in. */
export const CORNER_INSET = 12;

/** Where a tile of this size sits in a corner of the bounds; a top corner `top` down (selfTopInset), never below a bottom one. */
export function cornerPosition(corner: Corner, tile: Box, bounds: Box, top = CORNER_INSET): { x: number; y: number } {
  const x = corner.endsWith('l') ? CORNER_INSET : Math.max(CORNER_INSET, bounds.w - tile.w - CORNER_INSET);
  const bottom = Math.max(CORNER_INSET, bounds.h - tile.h - CORNER_INSET);
  const y = corner.startsWith('t') ? Math.min(top, bottom) : bottom;
  return { x, y };
}

/** The recording toast's band at the top of the stage: 12 down, 36 high (top-3, py-2, a 20 line). */
export const TOAST_BOTTOM = 48;

/**
 * How far down the stage a top corner's self tile stands: under what runs
 * along the stage's top, so nothing overlaps it. Under the recording toast's
 * band always (it comes and goes, and the tile should not jump for it), and
 * under the player's recommendation banner and the chips while they show.
 * `banner` and `chips` are their heights, 0 when not shown; the chips stand
 * 8 under the banner, or 12 down without it.
 */
export function selfTopInset(p: { banner: number; chips: number }): number {
  const bannerBottom = p.banner ? CORNER_INSET + p.banner : 0;
  const chipsBottom = p.chips ? (p.banner ? bannerBottom + 8 : CORNER_INSET) + p.chips : 0;
  return Math.max(TOAST_BOTTOM, bannerBottom, chipsBottom) + 8;
}

/** The self tile's corner, kept for the browser session (sessionStorage): bottom right until moved. */
const SELF_CORNER_KEY = 'call.selfCorner';
export function storedSelfCorner(store: Pick<Storage, 'getItem'> | null): Corner {
  try {
    const v = store?.getItem(SELF_CORNER_KEY);
    return v === 'tl' || v === 'tr' || v === 'bl' || v === 'br' ? v : 'br';
  } catch { return 'br'; }
}
export function storeSelfCorner(store: Pick<Storage, 'setItem'> | null, corner: Corner): void {
  try { store?.setItem(SELF_CORNER_KEY, corner); } catch { /* private mode: this call only */ }
}

/** The corner nearest where a dragged tile was let go, by its centre. */
export function nearestCorner(pos: { x: number; y: number }, tile: Box, bounds: Box): Corner {
  const cx = pos.x + tile.w / 2;
  const cy = pos.y + tile.h / 2;
  return `${cy < bounds.h / 2 ? 't' : 'b'}${cx < bounds.w / 2 ? 'l' : 'r'}` as Corner;
}

export type TrackEvent = 'start' | 'mute' | 'unmute' | 'ended' | 'inactive';

/**
 * Whether a shared screen's track is showing frames. A track just added
 * arrives muted and unmutes once media flows, so a mute is not the end of
 * it; ended and inactive are.
 */
export function trackLiveness(live: boolean, event: TrackEvent): boolean {
  switch (event) {
    case 'start': return true;
    case 'unmute': return true;
    case 'mute': return false;
    case 'ended': return false;
    case 'inactive': return false;
    default: return live;
  }
}

/** A presence entry is fresh for this long after its last heartbeat. */
export const PRESENCE_FRESH_MS = 90_000;

/**
 * Who is in a room now: active entries with a recent heartbeat. An entry
 * with no heartbeat yet is taken as present for the grace after it joined,
 * so a tab closed without Leave does not hold a banner up for ever.
 */
export function presentIds(
  entries: Array<{ id: string; active: boolean; lastSeenAtMs?: number | null; joinedAtMs?: number | null }>, nowMs: number,
): string[] {
  return entries
    .filter((e) => e.active && (nowMs - (e.lastSeenAtMs ?? e.joinedAtMs ?? nowMs)) < PRESENCE_FRESH_MS)
    .map((e) => e.id);
}

/**
 * The calls that someone other than this user is in right now, each with who
 * that is (presentUid): the "On the call" pill names them, whoever started
 * the room (decision 72).
 */
export function callsSomeoneIsIn<T extends { roomId: string }>(
  calls: T[], present: Record<string, string[] | undefined>, uid: string,
): Array<T & { presentUid: string }> {
  return calls.flatMap((c) => {
    const other = (present[c.roomId] ?? []).find((id) => id !== uid);
    return other ? [{ ...c, presentUid: other }] : [];
  });
}

/** A room chat line: trimmed, at most this long; nothing when empty. */
export const CHAT_MAX_CHARS = 500;
export function chatText(raw: string): string | null {
  const t = raw.replace(/\s+/g, ' ').trim();
  return t ? t.slice(0, CHAT_MAX_CHARS) : null;
}

/** The room keeps only the newest lines. */
export const CHAT_KEEP = 200;
export function keepChat<T>(lines: T[]): T[] {
  return lines.length > CHAT_KEEP ? lines.slice(lines.length - CHAT_KEEP) : lines;
}

/**
 * The camera to ask for, on every device and in every orientation: the
 * sensor's own landscape size. A phone's OS turns the frames itself as the
 * phone turns (iOS Safari rotates the sent track on device rotation), so
 * asking for a portrait size while upright turned it twice and sent a
 * landscape feed from an upright phone, and the reverse on its side. The
 * shape the other side shows is the one the frames arrive in, read from
 * the video element on each loadedmetadata and resize (fitBox).
 */
export function cameraConstraints(): MediaTrackConstraints {
  return { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } };
}

/** Chat lines from the other side since this side last had the chat open. */
export function unreadCount(lines: Array<{ from: string; at: string }>, me: string, seenAt: string | null): number {
  return lines.filter((l) => l.from !== me && (!seenAt || l.at > seenAt)).length;
}

/** How much of the pane the chat sheet covers on a phone. */
export const CHAT_SHEET_SHARE = 0.65;

/**
 * Where the other side's tile fits: the whole pane, or on a phone with the
 * chat sheet up, the part above it, so they stay in sight while chatting.
 */
export function tileArea(pane: Box, chatSheetUp: boolean): Box {
  return chatSheetUp ? { w: pane.w, h: Math.round(pane.h * (1 - CHAT_SHEET_SHARE)) } : pane;
}

/** The shared screen's tile is named as their screen, not as their camera (Meet). */
export function shareLabel(first: string): string {
  return `${first}'s screen`;
}

/**
 * Back on the tab: which of this side's tracks to start again. iOS stops the
 * camera while Safari is in the background, so the video track comes back
 * ended (or muted, with no frames); audio is kept wherever iOS let it run.
 */
export function tracksToRestart(tracks: Array<{ kind: string; readyState: string; muted: boolean }>): { video: boolean; audio: boolean } {
  const gone = (kind: string) => {
    const t = tracks.filter((x) => x.kind === kind);
    return t.length > 0 && t.every((x) => x.readyState === 'ended' || x.muted);
  };
  return { video: gone('video'), audio: gone('audio') };
}

/**
 * The chat is per call: a booked lesson's call is its booking, any other
 * call the session its host started on the room. Only this call's lines show.
 */
export function chatSessionOf(bookingId: string | undefined, roomSession: string | null | undefined): string | null {
  return bookingId ?? roomSession ?? null;
}
export function linesOfSession<T extends { session?: string }>(lines: T[], session: string | null): T[] {
  return session ? lines.filter((l) => l.session === session) : [];
}

/**
 * A callable not deployed yet, as Firebase reports a missing function: the
 * emulator's not-found, unimplemented, or a production 404 without CORS,
 * which the SDK surfaces as internal.
 */
export function callableMissing(err: unknown): boolean {
  const code = (err as { code?: string } | null)?.code ?? '';
  return code === 'functions/not-found' || code === 'functions/unimplemented' || code === 'functions/internal';
}

/**
 * The room's people: the one this side's lesson is with (by id, never just
 * the first to connect, which may be a guest), and anyone else in the room.
 * With anyone else there, the room is three tiles.
 */
export function splitParticipants<T extends { id: string }>(list: T[], otherId: string | undefined): { main: T | null; others: T[] } {
  const main = list.find((p) => p.id === otherId) ?? list[0] ?? null;
  return { main, others: list.filter((p) => p !== main) };
}

/**
 * A chat line sent over the room's data channel, beside its write to the
 * room: the other side shows it at once instead of waiting on its listener
 * (WebKit's took 8.4 s on staging, finding 4).
 */
export interface ChatWire<T> { t: 'chat'; line: T }
export function chatWire<T>(line: T): ChatWire<T> { return { t: 'chat', line }; }
export function isChatWire(data: unknown): data is ChatWire<{ id: string; from: string; name: string; text: string; at: string; session?: string }> {
  const line = (data as { line?: Record<string, unknown> } | null)?.line;
  return !!data && typeof data === 'object' && (data as { t?: unknown }).t === 'chat' && !!line
    && typeof line.id === 'string' && typeof line.text === 'string' && typeof line.at === 'string' && typeof line.from === 'string';
}

/** The room's stored lines and those that came over the channel: one each by id, this session's, oldest first. */
export function mergeChatLines<T extends { id: string; at: string; session?: string }>(stored: T[], live: T[], session: string | null): T[] {
  const byId = new Map<string, T>();
  for (const l of [...live, ...stored]) byId.set(l.id, l);
  return keepChat(linesOfSession([...byId.values()].sort((a, b) => a.at.localeCompare(b.at)), session));
}


/**
 * The stage (141): the other person's feed fills the stage's width,
 * letterboxed inside a tile that spans the call's whole width on the black
 * bezel. On a larger screen the stage stands 8 in, Meet's thin margin, with
 * the call tiles' radius 12 (LC-1); on a phone it is edge to edge, with no
 * inset and no rounded frame. Above the phone's chat sheet while it is up.
 */
export function stageBox(pane: Box, large: boolean, chatSheetUp: boolean): Box & { x: number; y: number; radius: number } {
  const area = tileArea(pane, chatSheetUp);
  const inset = large ? 8 : 0;
  return { x: inset, y: inset, w: Math.max(0, area.w - 2 * inset), h: Math.max(0, area.h - 2 * inset), radius: large ? 12 : 0 };
}

/**
 * The other person's name label, along the stage's bottom, on the side away
 * from the self tile and short of it, so the tile never covers it, in the
 * strip above the phone's chat sheet as on the full stage (job 5).
 */
export const NAME_LABEL_EDGE = 12;
export function nameLabelPlace(p: { stageW: number; selfShown: boolean; selfW: number; selfCorner: Corner }): { side: 'left' | 'right'; maxWidth: number } {
  // Plain text in the tile's corner (141), 12 in, with no pill around it.
  const edge = NAME_LABEL_EDGE;
  // Only a bottom corner's tile shares the bottom with it.
  const room = p.stageW - 2 * edge - (p.selfShown && p.selfCorner.startsWith('b') ? p.selfW + CORNER_INSET : 0);
  return { side: p.selfShown && p.selfCorner === 'bl' ? 'right' : 'left', maxWidth: Math.max(0, room) };
}

/**
 * The stage with a share beside the match (144). A share, theirs or this side's, takes the stage; the
 * match, if open, moves to the top of the side column with both cameras under it, still playing, and
 * starting or stopping the share never closes it. This side's own share shows its stage as "You're
 * sharing your screen" with Stop Sharing (Meet's presenter), unless theirs is already there. Without a
 * share, the match has the stage as before (104, 131, 133).
 */
export function stageLayout(p: { inCall: boolean; matchOpen: boolean; theirShare: boolean; ownShare: boolean; large: boolean; camerasHidden: boolean }): {
  shareOnStage: boolean; ownShareOnStage: boolean; matchInColumn: boolean; sidebar: boolean; phoneMatchRail: boolean; camerasOff: boolean;
} {
  const shareOnStage = p.inCall && (p.theirShare || p.ownShare);
  const matchInColumn = p.matchOpen && shareOnStage;
  return {
    shareOnStage,
    ownShareOnStage: p.inCall && p.ownShare && !p.theirShare,
    matchInColumn,
    sidebar: p.matchOpen || shareOnStage,
    phoneMatchRail: p.matchOpen && !p.large && !matchInColumn,
    camerasOff: p.matchOpen && p.camerasHidden && !matchInColumn,
  };
}

/**
 * What the floating window shows while the call's tab is in the background (143), as Meet's: the share
 * if the other side is sharing, else the match if it is open, else the other person; under it, small,
 * whoever is not already the main picture, then this side's own camera.
 */
export function pipLayout(p: { theirShare: boolean; matchOpen: boolean }): { main: 'share' | 'match' | 'them'; tiles: Array<'them' | 'you'> } {
  const main = p.theirShare ? 'share' : p.matchOpen ? 'match' : 'them';
  return { main, tiles: main === 'them' ? ['you'] : ['them', 'you'] };
}
